import { useCallback, useMemo, useState } from 'react';
import type { ScheduleOccurrenceSourceType } from '@/utils/scheduleOccurrences';

const STORAGE_KEY = 'courtsync.calendar.sourceFilters';
/** Set once play_event has been defaulted into stored prefs (or user has changed filters). */
const PLAY_EVENT_DEFAULT_MIGRATION_KEY = 'courtsync.calendar.sourceFilters.playEventDefaulted';

function readStoredFilters(): ScheduleOccurrenceSourceType[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const allowed: ScheduleOccurrenceSourceType[] = ['lesson', 'court_hire', 'closure', 'play_event'];
    return parsed.filter((v): v is ScheduleOccurrenceSourceType =>
      allowed.includes(v as ScheduleOccurrenceSourceType)
    );
  } catch {
    return null;
  }
}

function writeStoredFilters(filters: ScheduleOccurrenceSourceType[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    localStorage.setItem(PLAY_EVENT_DEFAULT_MIGRATION_KEY, '1');
  } catch {
    // ignore quota errors
  }
}

function hasPlayEventDefaultMigration(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(PLAY_EVENT_DEFAULT_MIGRATION_KEY) === '1';
}

/** Resolve active filters: empty storage → all available types entitled. */
export function resolveActiveSourceFilters(
  storedFilters: ScheduleOccurrenceSourceType[],
  availableSourceTypes: ScheduleOccurrenceSourceType[]
): ScheduleOccurrenceSourceType[] {
  const storedActive = storedFilters.filter((t) => availableSourceTypes.includes(t));
  return storedActive.length > 0 ? storedActive : availableSourceTypes;
}

/**
 * Persisted source-type toggles for the unified management calendar.
 * Defaults to all types the user is entitled to see; play_event defaults on for legacy prefs.
 */
function resolveStoredSourceFilters(
  storedFilters: ScheduleOccurrenceSourceType[],
  availableSourceTypes: ScheduleOccurrenceSourceType[]
): ScheduleOccurrenceSourceType[] {
  const base = storedFilters.length > 0 ? storedFilters : (readStoredFilters() ?? []);
  if (base.length === 0) return base;

  const storedActive = base.filter((t) => availableSourceTypes.includes(t));
  if (storedActive.length === 0) return base;

  if (
    availableSourceTypes.includes('play_event') &&
    !storedActive.includes('play_event') &&
    !hasPlayEventDefaultMigration()
  ) {
    const next: ScheduleOccurrenceSourceType[] = [...storedActive, 'play_event'];
    writeStoredFilters(next);
    return next;
  }

  return storedActive;
}

export function useScheduleSourceTypeFilters(availableSourceTypes: ScheduleOccurrenceSourceType[]) {
  const [userFilters, setUserFilters] = useState<ScheduleOccurrenceSourceType[] | null>(null);

  const sourceFilters = useMemo(
    () => resolveStoredSourceFilters(userFilters ?? [], availableSourceTypes),
    [userFilters, availableSourceTypes]
  );

  const activeSourceFilters = useMemo(
    () => resolveActiveSourceFilters(sourceFilters, availableSourceTypes),
    [sourceFilters, availableSourceTypes]
  );

  const toggleSource = useCallback(
    (source: ScheduleOccurrenceSourceType) => {
      const current = resolveActiveSourceFilters(sourceFilters, availableSourceTypes);

      let next: ScheduleOccurrenceSourceType[];
      if (current.includes(source)) {
        if (current.length <= 1) return;
        next = current.filter((s) => s !== source);
      } else {
        next = [...current, source];
      }
      writeStoredFilters(next);
      setUserFilters(next);
    },
    [availableSourceTypes, sourceFilters]
  );

  const isSourceActive = useCallback(
    (source: ScheduleOccurrenceSourceType) => activeSourceFilters.includes(source),
    [activeSourceFilters]
  );

  return {
    activeSourceFilters,
    toggleSource,
    isSourceActive,
  };
}
